import React, { useEffect, useRef, useState } from "react";

/**
 * Reveal — fades + lifts its children into view when scrolled into the viewport.
 * Props:
 *   delay  : ms before the animation runs after entering view
 *   from   : "up" | "left" | "right" | "scale"
 *   as     : tag/component (default div)
 *   once   : default true; if false, replays on re-enter
 */
const Reveal = ({ children, delay = 0, from = "up", as: Tag = "div", once = true, className = "", style, ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) obs.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.12 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [once]);

  const initial = {
    up: "translate3d(0,28px,0)",
    left: "translate3d(-28px,0,0)",
    right: "translate3d(28px,0,0)",
    scale: "scale(0.96)",
  }[from] || "translate3d(0,24px,0)";

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        transition: "opacity 700ms cubic-bezier(.2,.7,.2,1), transform 700ms cubic-bezier(.2,.7,.2,1)",
        transitionDelay: `${delay}ms`,
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : initial,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
